import React, { useState } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { trackException } from '../services/appInsightsService';

const BASE_URL = () =>
    (process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api');

/**
 * UnlimitedPlanAssignment — Admin form to assign plan_unlimited_v1 to a tenant.
 * Calls POST /platform/assign-unlimited-plan.
 */
const UnlimitedPlanAssignment = () => {
    const [tenantId, setTenantId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [result, setResult] = useState(null);

    const handleAssign = async (e) => {
        e.preventDefault();
        if (!tenantId.trim()) {
            setStatus({ type: 'error', message: 'Tenant ID is required.' });
            return;
        }

        setIsLoading(true);
        setStatus({ type: '', message: '' });
        setResult(null);

        try {
            const response = await fetchWithAuth(`${BASE_URL()}/platform/assign-unlimited-plan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantId: tenantId.trim() })
            });

            if (response.ok) {
                const data = await response.json();
                setResult(data);
                setStatus({ type: 'success', message: `Unlimited plan assigned to ${tenantId.trim()} successfully.` });
                setTenantId('');
            } else {
                const errText = await response.text();
                setStatus({ type: 'error', message: `Assignment failed: ${errText}` });
            }
        } catch (err) {
            trackException(err, { operation: 'UnlimitedPlanAssignment.handleAssign', tenantId });
            setStatus({ type: 'error', message: err.message });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: 16, border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, maxWidth: 480 }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Assign Unlimited Plan</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', margin: '0 0 12px 0' }}>
                Assigns <code>plan_unlimited_v1</code> to the specified tenant, removing all billing limits.
            </p>

            <form onSubmit={handleAssign}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Tenant ID</span>
                    <input
                        type="text"
                        value={tenantId}
                        onChange={e => setTenantId(e.target.value)}
                        placeholder="e.g. tenant_abc123"
                        style={{
                            padding: '6px 10px',
                            border: '1px solid var(--border-color, #d1d5db)',
                            borderRadius: 4,
                            fontSize: 13,
                            color: 'var(--text-primary, #111827)',
                        }}
                    />
                </label>

                {status.message && (
                    <div style={{
                        padding: '6px 10px',
                        marginBottom: 12,
                        borderRadius: 4,
                        // `--error`/`--success` are the same static hex in both themes, so neither can
                        // serve as TEXT on a background tinted from --bg-card -- same pattern as
                        // RealtimeProviderPolicySettings.js's noticeStyle / PlanEditor.js.
                        background: status.type === 'error'
                            ? 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card, #fff))'
                            : 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card, #fff))',
                        color: 'var(--text-primary, #111827)',
                        fontSize: 13
                    }}>
                        {status.message}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={isLoading || !tenantId.trim()}
                    style={{
                        background: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        padding: '7px 16px',
                        borderRadius: 4,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                        fontSize: 13
                    }}
                >
                    {isLoading ? 'Assigning...' : 'Assign Unlimited Plan'}
                </button>
            </form>

            {result && (
                <div style={{ marginTop: 12, padding: '8px 10px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: 4, fontSize: 12 }}>
                    <strong>Result:</strong>
                    <pre style={{ margin: '4px 0 0 0', overflowX: 'auto' }}>
                        {JSON.stringify({ tenantId: result.tenantId, planId: result.plan?.planId, tier: result.plan?.tier }, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default UnlimitedPlanAssignment;
